<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useClientAccountStore } from '@/stores/clientAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    public: true,
    clientArea: true,
  },
})

const accountStore = useClientAccountStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const isPasswordVisible = ref(false)
const formError = ref('')
const successMessage = ref('')
const loading = ref(false)
const refVForm = ref()

const form = ref({
  password: '',
  passwordConfirmation: '',
})

const token = computed(() => String(route.query.reset_password_token || ''))

const submit = async () => {
  formError.value = ''
  successMessage.value = ''
  loading.value = true
  try {
    const result = await accountStore.resetPassword({
      resetPasswordToken: token.value,
      password: form.value.password,
      passwordConfirmation: form.value.passwordConfirmation,
    })

    successMessage.value = result.message
    setTimeout(() => router.push('/client/login'), 1500)
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || t('clientResetPassword.genericError')
  }
  finally {
    loading.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      submit()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('clientResetPassword.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('clientResetPassword.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="!token"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ $t('clientResetPassword.missingToken') }}
        </VAlert>

        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ successMessage }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.password"
                :label="$t('common.newPassword')"
                autocomplete="new-password"
                :rules="[requiredValidator, passwordValidator]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                :disabled="loading || !token"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.passwordConfirmation"
                :label="$t('common.confirmNewPassword')"
                autocomplete="new-password"
                :rules="[requiredValidator, confirmedValidator(form.passwordConfirmation, form.password)]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :disabled="loading || !token"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                color="primary"
                :loading="loading"
                :disabled="!token"
              >
                {{ $t('clientResetPassword.submit') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
